import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { EnvironmentView, ScenePlan } from "@pushback-and-chaos/contracts";
import { EnvironmentDefinitionSchema, type EnvironmentDefinition } from "@pushback-and-chaos/contracts";
import { composeLivingSection, composeSection, ensureSceneSymbols, outlineSymbols, toSvgString, type BackplateAsset, type SpriteSymbol } from "@pushback-and-chaos/environment";
import { ENVIRONMENT_BACKPLATES, ENVIRONMENT_DEFINITIONS, ENVIRONMENT_PACKS } from "../generated/environment-assets.generated.js";
import { attachSectionAnimation } from "./animate.js";
import { playPlan, transitionViewFor } from "./playback.js";
import { optimizeSvgDelivery } from "./responsive-image.js";
import { livingEnvironmentEnabled, livingKits } from "./living-policy.js";

/**
 * THE SECTION (ADR 0009): the animated architectural section drawing that
 * replaces the grid-paper placeholder band. The client's entire job here is
 * to DRAW the server-derived EnvironmentView — the composer is a pure
 * function from (definition, view, packs) and this component adds only the
 * ambient animation layer and the cross-fade between environments.
 *
 * Symbol geometry is inlined at build time. Registered paintings use
 * responsive, content-addressed image assets with truthful recovery.
 *
 * Rendered via innerHTML rather than React elements deliberately: the
 * composer emits presentation-attribute SVG (stroke-width and friends in
 * their native spelling), the animation layer mutates transform attributes
 * outside React's knowledge, and reconciling that through JSX would fight
 * both. The markup is our own generated output, not remote content.
 */

const SYMBOLS: ReadonlyMap<string, SpriteSymbol> = (() => {
  const map = new Map<string, SpriteSymbol>();
  for (const pack of ENVIRONMENT_PACKS) {
    const manifest = pack.manifest as { symbols: readonly SpriteSymbol["meta"][] };
    for (const meta of manifest.symbols) {
      const markup = (pack.symbols as Record<string, string>)[meta.id];
      if (markup !== undefined) map.set(meta.id, { meta, markup });
    }
  }
  return map;
})();

const DEFINITIONS: ReadonlyMap<string, EnvironmentDefinition> = (() => {
  const map = new Map<string, EnvironmentDefinition>();
  for (const raw of Object.values(ENVIRONMENT_DEFINITIONS)) {
    const parsed = EnvironmentDefinitionSchema.safeParse(raw);
    if (parsed.success) map.set(parsed.data.envId, parsed.data);
  }
  return map;
})();

export function environmentDefinitionFor(envId: string): EnvironmentDefinition | null {
  return DEFINITIONS.get(envId) ?? null;
}

/**
 * A case's own scene environment, by slug: the bundled definition whose id is
 * `env.<type>.<slug>` (the clinic for harborlight). Null when the case has no
 * authored scene yet — the library then shows the card without art, never a
 * borrowed building.
 */
export function sceneEnvironmentIdFor(caseSlug: string): string | null {
  for (const envId of DEFINITIONS.keys()) {
    const parts = envId.split(".");
    if (parts.length === 3 && parts[0] === "env" && parts[2] === caseSlug) return envId;
  }
  return null;
}

/** The bundled sprite symbols, for other surfaces that compose sections (the case library's card art). */
export function environmentSymbols(): ReadonlyMap<string, SpriteSymbol> {
  return SYMBOLS;
}

/**
 * The section carries no mechanics, toggle or not (2026-09-16). The opt-in
 * "show mechanics" line (CLAUDE.md Law 2's one deliberate exception) is drawn
 * once, under each turn's narration on the thread, from the history record;
 * the strip that used to sit over this drawing was a second copy of it. The
 * section leak test still scans this surface in both directions.
 */
/**
 * `tone` (home pass, 2026-09-09): "ink" draws the section as the band does —
 * ink line work on grid paper, the red string as its one accent. "faded"
 * draws EVERY stroke in that one accent, lightly, on no paper of its own:
 * the drawing becomes the ground the front door stands on, present but never
 * competing with what is in front of it. Colour only — the geometry, the
 * ambient animation and the adaptive reveal are exactly the band's.
 */
export type SectionTone = "ink" | "faded";

/** Play compresses the planner's physical walking time; a crossing of the floor reads in a few seconds, not twenty. */
const PLAY_SPEED = 3;

/**
 * `plan` (ADR 0014): the turn's transient ScenePlan. When one arrives with
 * a new view, the section first draws the TRANSITION view (the new view
 * plus anyone who left, still at the exit), plays the plan over it, then
 * draws the true view. The plan is played once per arrival and never
 * derived here; a resumed session gets no plan and draws the result.
 */
/**
 * The rooms this environment can be framed on, if any — the "closer view" a
 * surface may offer somewhere other than over the drawing. Empty for an
 * environment that is not promoted to layers, which is the honest answer:
 * there is no safe closer view to give.
 */
export function sceneFramingRooms(envId: string, representation: "auto" | "composed" | "layered" = "auto"): readonly { readonly room: string; readonly label: string }[] {
  const layered = representation === "layered" || (representation === "auto" && livingEnvironmentEnabled(envId));
  const kit = livingKits.get(envId) ?? null;
  if (!layered || kit === null) return [];
  const definition = DEFINITIONS.get(envId);
  return kit.rooms.map((room) => ({ room: room.room, label: definition?.rooms.find((item) => item.id === room.room)?.label ?? "Room" }));
}

/** Where the scene is framed. `room: null` is the whole building. */
export interface SceneFraming {
  readonly envId: string;
  readonly room: string | null;
  /** A named closer crop inside the room (a station or a wall); absent is the room's own default. */
  readonly crop?: string;
}

export function EnvironmentSection({
  view,
  plan = null,
  playbackSpeed = PLAY_SPEED,
  tone = "ink",
  representation = "auto",
  delivery = "optimized",
  initialRoom,
  framing: controlledFraming,
  onFraming,
  controls = "overlay",
}: {
  readonly view: EnvironmentView;
  readonly plan?: ScenePlan | null;
  readonly playbackSpeed?: number;
  readonly tone?: SectionTone;
  readonly representation?: "auto" | "composed" | "layered";
  readonly delivery?: "original" | "optimized";
  readonly initialRoom?: string;
  /**
   * Framing, lifted (the Storyteller play screen, 2026-09-17). Supplied, the
   * owner decides where the closer view control lives and what it is set to;
   * omitted, the section keeps its own state exactly as it always has.
   */
  readonly framing?: SceneFraming | null;
  readonly onFraming?: (next: SceneFraming | null) => void;
  /** "none" leaves the framing buttons to the owner rather than drawing them over the art. */
  readonly controls?: "overlay" | "none";
}): ReactNode {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const previousView = useRef<EnvironmentView | null>(null);
  const [failedAssets, setFailedAssets] = useState<ReadonlySet<string>>(() => new Set());
  // Hard Light that could not load its reviewed backdrop falls back to Soft Wash
  // for that environment, never to a partial dramatic composition (ADR 0017).
  const [unlit, setUnlit] = useState<ReadonlySet<string>>(() => new Set());
  const litRef = useRef(false);
  const [ownFraming, setOwnFraming] = useState<SceneFraming | null>(() => initialRoom === undefined ? null : { envId: view.envId, room: initialRoom, crop: "overview" });
  const framing = controlledFraming === undefined ? ownFraming : controlledFraming;
  /** null follows the scene: the camera goes wherever the player is. */
  const setFraming = (next: SceneFraming | null): void => {
    if (onFraming === undefined) setOwnFraming(next);
    else onFraming(next);
  };
  const [transition, setTransition] = useState<{ readonly view: EnvironmentView; readonly plan: ScenePlan } | null>(null);
  const layered = representation === "layered" || (representation === "auto" && livingEnvironmentEnabled(view.envId));
  const broken = failedAssets.has(view.envId);
  const kit = livingKits.get(view.envId) ?? null;

  // A new view with a plan: stage the transition before the swap is seen.
  // Reduced motion draws the result straight away (discrete states still change).
  useEffect(() => {
    const before = previousView.current;
    previousView.current = view;
    setTransition(null);
    // Release A paints admitted discrete states. A seated cutout is not a walk rig.
    if (layered || broken) return;
    if (plan === null || plan.steps.length === 0 || before === null || before.envId !== view.envId || before === view) return;
    if (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const definition = DEFINITIONS.get(view.envId);
    if (definition === undefined) return;
    setTransition({ view: transitionViewFor(definition, before, view, plan), plan });
  }, [view, plan, layered, broken]);

  const shown = layered || broken ? view : transition?.view ?? view;
  const drawn = shown.lighting === "hard-light" && unlit.has(shown.envId) ? { ...shown, lighting: "soft-wash" as const } : shown;
  const composed = useMemo(() => {
    const definition = DEFINITIONS.get(drawn.envId);
    if (definition === undefined) return null;
    const backplates: BackplateAsset[] = [];
    for (const b of ENVIRONMENT_BACKPLATES[drawn.envId] ?? []) {
      if (b.treatment === "day" || b.treatment === "night") backplates.push({ treatment: b.treatment, href: b.href, frame: b.frame });
    }
    const symbols = ensureSceneSymbols(SYMBOLS, drawn);
    const living = layered && !broken ? composeLivingSection({ definition, view: drawn, symbols, kit, ...(framing?.envId === drawn.envId ? { focus: framing.room, ...(framing.crop === undefined ? {} : { crop: framing.crop }) } : {}) }) : null;
    const doc = living?.document ?? composeSection({ definition, view: drawn, symbols: broken ? outlineSymbols(symbols, drawn) : symbols, backplates: broken ? [] : backplates, treatment: "day" });
    const markup = toSvgString(doc, { id: "pc-environment-section" });
    return { markup: delivery === "original" ? markup : optimizeSvgDelivery(markup, typeof window === "undefined" ? 1536 : window.innerWidth * window.devicePixelRatio), living };
  }, [drawn, delivery, layered, broken, kit, framing]);
  const svg = composed?.markup ?? null;
  useEffect(() => {
    litRef.current = composed?.living?.lighting === "hard-light";
  }, [composed]);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    const recover = (event: Event): void => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const fallback = target.getAttribute("data-png-fallback");
      if (fallback === null) {
        if (target.tagName.toLowerCase() !== "image") return;
        if (litRef.current) setUnlit((previous) => new Set([...previous, view.envId]));
        else setFailedAssets((previous) => new Set([...previous, view.envId]));
        return;
      }
      target.removeAttribute("data-png-fallback");
      target.setAttribute("href", fallback);
    };
    container.addEventListener("error", recover, true);
    return () => container.removeEventListener("error", recover, true);
  }, [view.envId]);

  useEffect(() => {
    if (transition === null) return;
    const container = containerRef.current;
    if (container === null) return;
    // One frame so the transition view's SVG is in the DOM before positions are read.
    let stop: (() => void) | null = null;
    const raf = requestAnimationFrame(() => {
      stop = playPlan(container, transition.plan, playbackSpeed, () => setTransition(null));
    });
    return () => {
      cancelAnimationFrame(raf);
      stop?.();
    };
  }, [transition, playbackSpeed]);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    return attachSectionAnimation(container);
  }, []);

  // ADAPTIVE REVEAL: the authored crop is much wider than the band, and an
  // aspect-locked SVG letterboxes the difference into blank paper. When the
  // composer marks a focused drawing (data-crop-*/data-reveal-*), match the
  // viewBox height to the band's measured proportions — extending toward
  // the reveal extents shows MORE NEIGHBOR FLOORS above and below the
  // office, never blank space. The crop clip is widened in step so the
  // revealed floors actually paint. Purely presentational; one attribute
  // write per resize.
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    const apply = (): void => {
      const box = container.getBoundingClientRect();
      if (box.width <= 0 || box.height <= 0) return;
      for (const el of container.querySelectorAll<SVGSVGElement>("svg[data-crop-h]")) {
        const cropY = Number(el.getAttribute("data-crop-y"));
        const cropH = Number(el.getAttribute("data-crop-h"));
        const minY = Number(el.getAttribute("data-reveal-min"));
        const maxY = Number(el.getAttribute("data-reveal-max"));
        const vb = (el.getAttribute("viewBox") ?? "").split(" ").map(Number);
        const [vbX, , vbW] = [vb[0] ?? 0, 0, vb[2] ?? 0];
        if (!Number.isFinite(cropH) || cropH <= 0 || vbW <= 0) continue;
        // Never shrink below the authored crop; never extend past the building.
        const targetH = Math.min(Math.max((vbW * box.height) / box.width, cropH), maxY - minY);
        const extra = targetH - cropH;
        let top = cropY - extra / 2;
        let bottom = cropY + cropH + extra / 2;
        if (top < minY) { bottom += minY - top; top = minY; }
        if (bottom > maxY) { top -= bottom - maxY; bottom = maxY; }
        top = Math.max(top, minY);
        el.setAttribute("viewBox", `${String(vbX)} ${String(Math.round(top * 100) / 100)} ${String(vbW)} ${String(Math.round((bottom - top) * 100) / 100)}`);
        const clip = el.querySelector<SVGRectElement>("clipPath rect");
        if (clip !== null) {
          clip.setAttribute("y", String(Math.round(top * 100) / 100));
          clip.setAttribute("height", String(Math.round((bottom - top) * 100) / 100));
        }
      }
    };
    apply();
    // jsdom (the leak tests' DOM) has no ResizeObserver; the first apply
    // above still runs there.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(apply);
    observer.observe(container);
    return () => { observer.disconnect(); };
  }, [svg]);

  if (svg === null) return null;

  return (
    <div
      ref={containerRef}
      data-testid="environment-section"
      data-tone={tone}
      data-representation={broken ? "simplified" : composed?.living?.version != null ? "layered" : "composed"}
      data-art-version={composed?.living?.version ?? undefined}
      data-lighting={composed?.living?.lighting ?? "soft-wash"}
      data-coverage-gaps={composed?.living?.gaps.join(",") || undefined}
      className="relative h-full w-full overflow-hidden"
      style={
        tone === "faded"
          ? // Every stroke in the accent, the sky's masking rects in the paper the
            // page already has, and the whole drawing held back so the panel in
            // front of it reads first. The custom property override reaches the
            // composer's `var(--pc-grid-bg, …)` fills through inheritance.
            { color: "var(--pc-string)", background: "transparent", opacity: 0.5, ["--pc-grid-bg" as string]: "var(--pc-paper)" }
          : // The section's paper and ink (ADR 0015 step 3); the grid-bg override
            // turns every symbol's occlusion casing the same neutral paper as the ground.
            { color: "var(--pc-section-ink)", background: "var(--pc-section-paper)", ["--pc-grid-bg" as string]: "var(--pc-section-paper)" }
      }
    >
      <div
        key={drawn.envId}
        className="absolute inset-0 pc-env-fade"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {layered && kit !== null && tone !== "faded" && controls === "overlay" ? <div className="absolute top-2 right-2 z-20 flex gap-1" aria-label="Scene framing" style={{ maxWidth: "calc(100% - 16px)" }}>
        <select aria-label="Scene room" className="min-h-11 rounded px-2 text-micro" style={{ background: "var(--pc-section-paper)", color: "var(--pc-section-ink)", maxWidth: "min(52vw, 190px)" }} value={framing?.envId === view.envId ? framing.room ?? "whole" : "follow"} onChange={(event) => setFraming(event.target.value === "follow" ? null : { envId: view.envId, room: event.target.value === "whole" ? null : event.target.value, crop: "overview" })}>
          <option value="follow">Follow the scene</option>
          {kit.rooms.map((room) => <option key={room.room} value={room.room}>{DEFINITIONS.get(view.envId)?.rooms.find((item) => item.id === room.room)?.label ?? "Room"}</option>)}
          <option value="whole">Whole building</option>
        </select>
        {framing?.envId === view.envId && framing.room !== null ? <select aria-label="Scene detail" className="min-h-11 rounded px-2 text-micro" style={{ background: "var(--pc-section-paper)", color: "var(--pc-section-ink)", maxWidth: "min(40vw, 170px)" }} value={framing.crop ?? "overview"} onChange={(event) => setFraming({ ...framing, crop: event.target.value })}>
          <option value="overview">Room overview</option>
          {kit.rooms.find((room) => room.room === framing.room)?.crops?.map((crop) => <option key={crop.id} value={crop.id}>{crop.label}</option>)}
        </select> : null}
      </div> : null}
    </div>
  );
}
